import type { PracticeEngineDeps, PracticeMode } from "./engine.ts";
import { validateSessionSize } from "./engine.ts";
import { firstCardGradeEvent } from "./events.ts";
import {
  cardHeldExplanation,
  cardTargetSnapshot,
  classifyCardPresentation,
  compareInstants,
  isCardClockBackward,
  isDue,
  nextCardSchedule,
  practiceStateFor,
  selectCardMeaning,
  type CardHeldReason,
} from "./scheduling.ts";
import { findMeaningById, findWordById, meaningsForWord } from "../vocabulary/grouping.ts";
import { hasValidOutgoingRelation } from "../vocabulary/relations.ts";
import type {
  CardActionKind,
  CardDirection,
  CardDirectionSchedule,
  CardGradeOutcome,
  CardReviewEvent,
  MeaningCard,
  MeaningPracticeState,
  VocabularySnapshot,
  WordEntry,
  WordType,
} from "../vocabulary/types.ts";
import { CARD_POLICY_VERSION } from "../vocabulary/types.ts";

export type { CardHeldReason };
export { cardHeldExplanation };

export interface CapturedCardContent {
  wordDisplay: string;
  normalizedText: string;
  type: WordType;
  shortLabel: string;
  definition: string;
  example: string;
  russianHint: string | null;
  memoryKey: string | null;
  collocations: string[];
  ambiguous: boolean;
}

export interface CardAttempt {
  itemId: string;
  attemptId: string;
  meaningId: string;
  contentRevision: number;
  direction: CardDirection;
  presentedAt: string;
  revealedAt: string | null;
  contextUsed: boolean;
  contextShownAt: string | null;
  wasDue: boolean;
  wasSpaced: boolean;
  isFirstAssessment: boolean;
  bucket: "scheduled" | "extra";
  captured: CapturedCardContent;
  result: "correct" | "incorrect" | "skipped" | null;
  heldReason: CardHeldReason | null;
  nextDueAt?: string;
  nextStage?: number;
  expectedStateRevision: number;
  presentationEventId: string;
  lastActionId: string;
  lastActionAt: string;
  gradeEventId: string | null;
  skipEventId: string | null;
}

export type CardSessionPhase =
  | "hidden"
  | "revealed"
  | "saving"
  | "save_failed"
  | "conflict"
  | "clock_blocked"
  | "review"
  | "completed";

export interface CardSessionState {
  format: "cards";
  sessionId: string;
  mode: PracticeMode;
  direction: CardDirection;
  startedAt: string;
  vocabularyRevision: number;
  selectedMeaningIds: string[];
  maxCards: number;
  items: CardAttempt[];
  cursor: number;
  phase: CardSessionPhase;
  status: "active" | "completed";
  pendingEvent?: CardReviewEvent;
  endedExplicitly: boolean;
  browsingHistory: boolean;
  unsavedExit: boolean;
  clockMessage?: string;
  conflictMessage?: string;
  persistError?: string;
  revealHint?: string;
}

export interface CardCandidate {
  meaning: MeaningCard;
  word: WordEntry;
  state: MeaningPracticeState;
}

export interface CardSummaryItem {
  itemId: string;
  index: number;
  meaningId: string;
  wordDisplay: string;
  shortLabel: string;
  type: WordType;
  result: CardAttempt["result"];
  heldReason: CardHeldReason | null;
  wasDue: boolean;
  nextDueAt?: string;
  nextStage?: number;
  direction: CardDirection;
}

export interface CardSummary {
  direction: CardDirection;
  correctCount: number;
  incorrectCount: number;
  skippedCount: number;
  unfinishedCount: number;
  items: CardSummaryItem[];
}

const CLOCK_MESSAGE =
  "This device clock moved backwards. Practice results are paused until the time looks consistent.";

export function cueContainsTarget(cue: string, normalizedTarget: string): boolean {
  const target = tokenize(normalizedTarget);
  if (target.length === 0) {
    return false;
  }
  const haystack = tokenize(cue);
  if (haystack.length < target.length) {
    return false;
  }
  for (let start = 0; start <= haystack.length - target.length; start += 1) {
    let matches = true;
    for (let offset = 0; offset < target.length; offset += 1) {
      if (haystack[start + offset] !== target[offset]) {
        matches = false;
        break;
      }
    }
    if (matches) {
      return true;
    }
  }
  return false;
}

export function cardIneligibleReason(
  snapshot: VocabularySnapshot,
  meaning: MeaningCard,
  direction: CardDirection,
): string | null {
  const word = findWordById(snapshot, meaning.wordId);
  if (!word) {
    return "The word for this meaning is missing.";
  }
  if (!word.displayText.trim() || !word.normalizedText.trim()) {
    return "This card has no word or phrase.";
  }
  if (!meaning.definition.trim()) {
    return "This card has no definition.";
  }
  if (direction === "hide_word" && cueContainsTarget(meaning.definition, word.normalizedText)) {
    return `The definition contains “${word.displayText}”, so hiding the word would show the answer.`;
  }
  return null;
}

export function hiddenWordLeakCount(snapshot: VocabularySnapshot): number {
  return snapshot.meanings.filter((meaning) => {
    const word = findWordById(snapshot, meaning.wordId);
    if (!word || !word.displayText.trim() || !meaning.definition.trim()) {
      return false;
    }
    return cueContainsTarget(meaning.definition, word.normalizedText);
  }).length;
}

export function eligibleCardMeanings(
  snapshot: VocabularySnapshot,
  mode: PracticeMode,
  direction: CardDirection,
  selectedMeaningIds: readonly string[] = [],
  now?: string,
): CardCandidate[] {
  const at =
    now ??
    snapshot.schedulerState.lastObservedAt ??
    snapshot.meanings[0]?.createdAt ??
    "1970-01-01T00:00:00.000Z";
  const selected = new Set(selectedMeaningIds);
  const candidates: CardCandidate[] = [];
  for (const meaning of snapshot.meanings) {
    if (cardIneligibleReason(snapshot, meaning, direction)) {
      continue;
    }
    const word = findWordById(snapshot, meaning.wordId);
    if (!word) {
      continue;
    }
    if (mode === "confusable" && !hasValidOutgoingRelation(snapshot, meaning.id)) {
      continue;
    }
    if (mode === "select" && !selected.has(meaning.id)) {
      continue;
    }
    const state = practiceStateFor(snapshot, meaning.id);
    if (mode === "due" && !isDue(state.cardSchedules[direction].dueAt, at)) {
      continue;
    }
    candidates.push({ meaning, word, state });
  }
  return candidates;
}

export function dueCardMeanings(
  snapshot: VocabularySnapshot,
  direction: CardDirection,
  now: string,
): CardCandidate[] {
  return eligibleCardMeanings(snapshot, "due", direction, [], now);
}

export function nextCardReviewTime(
  snapshot: VocabularySnapshot,
  direction: CardDirection,
  now: string,
): string | null {
  const upcoming = eligibleCardMeanings(snapshot, "extra", direction, [], now).filter(
    (candidate) => !isDue(candidate.state.cardSchedules[direction].dueAt, now),
  );
  return upcoming.reduce<string | null>((earliest, candidate) => {
    const dueAt = candidate.state.cardSchedules[direction].dueAt;
    if (earliest == null || compareInstants(dueAt, earliest) < 0) {
      return dueAt;
    }
    return earliest;
  }, null);
}

export function canRevealCard(session: CardSessionState): boolean {
  return session.status === "active" && session.phase === "hidden" && currentIsOpenTail(session);
}

export function canGradeCard(session: CardSessionState): boolean {
  return (
    session.status === "active" &&
    (session.phase === "hidden" || session.phase === "revealed") &&
    currentIsOpenTail(session)
  );
}

export function canSkipCard(session: CardSessionState): boolean {
  return (
    session.status === "active" &&
    (session.phase === "hidden" || session.phase === "revealed") &&
    currentIsOpenTail(session)
  );
}

export function canGoPrevious(session: CardSessionState): boolean {
  return session.phase !== "saving" && session.cursor > 0;
}

export function canResumeCard(session: CardSessionState): boolean {
  if (session.phase === "saving") {
    return false;
  }
  if (session.cursor < session.items.length - 1) {
    return true;
  }
  return session.status === "completed" && session.browsingHistory;
}

export function startCardSession(
  snapshot: VocabularySnapshot,
  selection: {
    mode: PracticeMode;
    direction: CardDirection;
    size: number;
    selectedMeaningIds: string[];
  },
  deps: PracticeEngineDeps,
):
  | { ok: true; session: CardSessionState; event: CardReviewEvent }
  | { ok: false; message: string; nextReviewAt?: string } {
  const sizeCheck = validateSessionSize(selection.size);
  if (!sizeCheck.ok) {
    return sizeCheck;
  }
  const now = deps.now();
  const eligible = eligibleCardMeanings(
    snapshot,
    selection.mode,
    selection.direction,
    selection.selectedMeaningIds,
    now,
  );
  if (eligible.length === 0) {
    if (selection.mode === "due") {
      return {
        ok: false,
        message: "No card reviews are due in this direction.",
        nextReviewAt: nextCardReviewTime(snapshot, selection.direction, now) ?? undefined,
      };
    }
    return { ok: false, message: "No cards are available for this setup." };
  }
  const session: CardSessionState = {
    format: "cards",
    sessionId: deps.createId(),
    mode: selection.mode,
    direction: selection.direction,
    startedAt: now,
    vocabularyRevision: snapshot.dataRevision,
    selectedMeaningIds: selection.selectedMeaningIds,
    maxCards: selection.size,
    items: [],
    cursor: 0,
    phase: "hidden",
    status: "active",
    endedExplicitly: false,
    browsingHistory: false,
    unsavedExit: false,
  };
  const created = createPresentation(snapshot, session, deps);
  if (!created) {
    if (created === null && isCardClockBackward({
      knownObservedAt: snapshot.schedulerState.lastObservedAt,
      presentedAt: now,
      actionAt: now,
      previousActionAt: null,
    })) {
      return { ok: false, message: CLOCK_MESSAGE };
    }
    return { ok: false, message: "No cards are available for this setup." };
  }
  return {
    ok: true,
    event: created.event,
    session: {
      ...session,
      items: [created.item],
      cursor: 0,
      phase: "saving",
      pendingEvent: created.event,
    },
  };
}

export function revealCard(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  deps: PracticeEngineDeps,
): { session: CardSessionState; event?: CardReviewEvent } {
  if (!canRevealCard(session)) {
    return { session };
  }
  const item = tail(session);
  if (!item || item.revealedAt) {
    return { session };
  }
  const blocked = blockedByContent(snapshot, session, item);
  if (blocked) {
    return { session: blocked };
  }
  return queueAction(snapshot, session, item, "reveal", null, deps, (current, actionAt) => ({
    ...current,
    revealedAt: actionAt,
  }));
}

export function showCardContext(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  deps: PracticeEngineDeps,
): { session: CardSessionState; event?: CardReviewEvent } {
  if (!canRevealCard(session)) {
    return { session };
  }
  const item = tail(session);
  if (!item || item.contextUsed || item.direction !== "hide_meaning" || !item.captured.ambiguous) {
    return { session };
  }
  if (!item.captured.example.trim()) {
    return { session };
  }
  const blocked = blockedByContent(snapshot, session, item);
  if (blocked) {
    return { session: blocked };
  }
  return queueAction(snapshot, session, item, "context", null, deps, (current, actionAt) => ({
    ...current,
    contextUsed: true,
    contextShownAt: actionAt,
  }));
}

function gradeBaseline(snapshot: VocabularySnapshot, item: CardAttempt): CardDirectionSchedule | null {
  const first = firstCardGradeEvent(snapshot, item.attemptId);
  if (!first || first.direction !== item.direction || first.meaningId !== item.meaningId) {
    return null;
  }
  const current = practiceStateFor(snapshot, item.meaningId).cardSchedules[item.direction];
  return {
    stage: first.previousTarget.stage,
    dueAt: first.previousTarget.dueAt,
    lastAssessmentAt: first.previousTarget.lastAssessmentAt,
    lastPresentedAt: current.lastPresentedAt,
    presentationOrdinal: current.presentationOrdinal,
  };
}

function replaceCardGrade(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  item: CardAttempt,
  outcome: CardGradeOutcome,
  deps: PracticeEngineDeps,
): { session: CardSessionState; event?: CardReviewEvent } {
  if (session.status !== "active" || session.phase === "saving") {
    return { session };
  }
  if (
    session.phase === "clock_blocked" ||
    session.phase === "conflict" ||
    session.phase === "save_failed" ||
    session.phase === "completed"
  ) {
    return { session };
  }
  if (item.result === outcome) {
    return { session };
  }
  const blocked = blockedByContent(snapshot, session, item);
  if (blocked) {
    return { session: blocked };
  }
  const baseline = gradeBaseline(snapshot, item);
  if (!baseline) {
    return { session };
  }
  return queueAction(snapshot, session, item, "grade", outcome, deps, (current, actionAt) => ({
    ...current,
    heldReason: nextCardSchedule(baseline, {
      outcome,
      contextUsed: current.contextUsed,
      wasDue: current.wasDue,
      wasSpaced: current.wasSpaced,
      isFirstAssessment: current.isFirstAssessment,
      actionAt,
    }).heldReason,
  }));
}

export function gradeCard(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  outcome: CardGradeOutcome,
  deps: PracticeEngineDeps,
): { session: CardSessionState; event?: CardReviewEvent } {
  const currentItem = session.items[session.cursor];
  if (currentItem && (currentItem.result === "correct" || currentItem.result === "incorrect")) {
    return replaceCardGrade(snapshot, session, currentItem, outcome, deps);
  }
  if (!canGradeCard(session)) {
    return { session };
  }
  const item = tail(session);
  if (!item) {
    return { session };
  }
  const blocked = blockedByContent(snapshot, session, item);
  if (blocked) {
    return { session: blocked };
  }
  const actionAt = deps.now();
  const state = practiceStateFor(snapshot, item.meaningId);
  const transition = nextCardSchedule(state.cardSchedules[item.direction], {
    outcome,
    contextUsed: item.contextUsed,
    wasDue: item.wasDue,
    wasSpaced: item.wasSpaced,
    isFirstAssessment: item.isFirstAssessment,
    actionAt,
  });
  return queueAction(snapshot, session, item, "grade", outcome, deps, (current) => ({
    ...current,
    heldReason: transition.heldReason,
  }));
}

export function skipCard(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  deps: PracticeEngineDeps,
): { session: CardSessionState; event?: CardReviewEvent } {
  if (!canSkipCard(session)) {
    return { session };
  }
  const item = tail(session);
  if (!item) {
    return { session };
  }
  const blocked = blockedByContent(snapshot, session, item);
  if (blocked) {
    return { session: blocked };
  }
  return queueAction(snapshot, session, item, "skip", null, deps, (current) => current);
}

export function previousCard(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  deps: PracticeEngineDeps,
): { session: CardSessionState; event?: CardReviewEvent } {
  if (!canGoPrevious(session)) {
    return { session };
  }
  const cursor = session.cursor - 1;
  return landOn(snapshot, session, cursor, deps);
}

export function resumeCard(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  deps: PracticeEngineDeps,
): { session: CardSessionState; event?: CardReviewEvent } {
  if (session.phase === "saving") {
    return { session };
  }
  if (session.cursor >= session.items.length - 1) {
    if (session.status === "completed" && session.browsingHistory) {
      return {
        session: { ...session, browsingHistory: false, phase: "completed", revealHint: undefined },
      };
    }
    return { session };
  }
  return landOn(snapshot, session, session.cursor + 1, deps);
}

export function endCardSession(session: CardSessionState, explicit: boolean): CardSessionState {
  const unsaved = session.phase === "saving" || session.phase === "save_failed";
  return {
    ...session,
    status: "completed",
    phase: "completed",
    endedExplicitly: explicit,
    browsingHistory: false,
    unsavedExit: unsaved,
    pendingEvent: unsaved ? session.pendingEvent : undefined,
    revealHint: undefined,
  };
}

export function openCardHistory(session: CardSessionState, index: number): CardSessionState {
  if (session.status !== "completed" || index < 0 || index >= session.items.length) {
    return session;
  }
  return {
    ...session,
    cursor: index,
    browsingHistory: true,
    phase: "review",
    revealHint: undefined,
  };
}

export function closeCardHistory(session: CardSessionState): CardSessionState {
  if (session.status !== "completed") {
    return session;
  }
  return { ...session, browsingHistory: false, phase: "completed", revealHint: undefined };
}

export function noteCardCommit(
  session: CardSessionState,
  event: CardReviewEvent,
  snapshot: VocabularySnapshot,
): CardSessionState {
  const saved = practiceStateFor(snapshot, event.meaningId);
  const card = saved.cardSchedules[event.direction];
  const items = session.items.map((item) => {
    if (item.attemptId !== event.attemptId) {
      return item;
    }
    const next: CardAttempt = {
      ...item,
      expectedStateRevision: saved.stateRevision,
      lastActionId: event.eventId,
      lastActionAt: event.actionAt,
    };
    if (event.actionKind === "reveal") {
      next.revealedAt = event.actionAt;
    }
    if (event.actionKind === "context") {
      next.contextUsed = true;
      next.contextShownAt = event.actionAt;
    }
    if (event.actionKind === "grade" && event.outcome) {
      next.result = event.outcome;
      next.gradeEventId = event.eventId;
      next.nextDueAt = card.dueAt;
      next.nextStage = card.stage;
    }
    if (event.actionKind === "skip") {
      next.result = "skipped";
      next.skipEventId = event.eventId;
    }
    return next;
  });
  const nextSession: CardSessionState = {
    ...session,
    items,
    pendingEvent: undefined,
    persistError: undefined,
    clockMessage: undefined,
    vocabularyRevision: snapshot.dataRevision,
    unsavedExit: false,
  };
  const cursorItem = items[nextSession.cursor];
  return {
    ...nextSession,
    phase: cursorItem ? phaseForItem(nextSession, cursorItem) : nextSession.phase,
  };
}

export function continueAfterResolution(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  deps: PracticeEngineDeps,
): { session: CardSessionState; event?: CardReviewEvent } {
  if (session.status !== "active") {
    return { session };
  }
  if (session.cursor < session.items.length - 1) {
    return landOn(snapshot, session, session.cursor + 1, deps);
  }
  const last = session.items[session.items.length - 1];
  if (!last?.result) {
    return { session };
  }
  if (session.items.length >= session.maxCards) {
    return { session: finishSession(session) };
  }
  const created = createPresentation(snapshot, session, deps);
  if (!created) {
    const now = deps.now();
    if (
      isCardClockBackward({
        knownObservedAt: snapshot.schedulerState.lastObservedAt,
        presentedAt: now,
        actionAt: now,
        previousActionAt: null,
      })
    ) {
      return { session: { ...session, phase: "clock_blocked", clockMessage: CLOCK_MESSAGE } };
    }
    return { session: finishSession(session) };
  }
  return {
    event: created.event,
    session: {
      ...session,
      items: [...session.items, created.item],
      cursor: session.items.length,
      phase: "saving",
      pendingEvent: created.event,
      browsingHistory: false,
      revealHint: undefined,
    },
  };
}

export function failCardSave(
  session: CardSessionState,
  message: string,
  kind: "save_failed" | "conflict" | "clock_blocked",
): CardSessionState {
  if (kind === "clock_blocked") {
    return { ...session, phase: "clock_blocked", clockMessage: message, persistError: undefined };
  }
  if (kind === "conflict") {
    return { ...session, phase: "conflict", conflictMessage: message, persistError: undefined };
  }
  return { ...session, phase: "save_failed", persistError: message };
}

export function buildCardSummary(session: CardSessionState): CardSummary {
  const items = session.items.map((item, index) => ({
    itemId: item.itemId,
    index,
    meaningId: item.meaningId,
    wordDisplay: item.captured.wordDisplay,
    shortLabel: item.captured.shortLabel,
    type: item.captured.type,
    result: item.result,
    heldReason: item.result === "correct" ? item.heldReason : null,
    wasDue: item.wasDue,
    nextDueAt: item.nextDueAt,
    nextStage: item.nextStage,
    direction: item.direction,
  }));
  return {
    direction: session.direction,
    correctCount: items.filter((item) => item.result === "correct").length,
    incorrectCount: items.filter((item) => item.result === "incorrect").length,
    skippedCount: items.filter((item) => item.result === "skipped").length,
    unfinishedCount: items.filter((item) => item.result == null).length,
    items,
  };
}

export function cardProgressLabel(session: CardSessionState): { current: number; total: number } {
  return {
    current: Math.min(session.cursor + 1, Math.max(session.items.length, 1)),
    total: session.maxCards,
  };
}

function tokenize(value: string): string[] {
  return value
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .split(/[^a-z0-9']+/)
    .filter((token) => token.length > 0);
}

function tail(session: CardSessionState): CardAttempt | undefined {
  if (session.cursor !== session.items.length - 1) {
    return undefined;
  }
  return session.items[session.cursor];
}

function currentIsOpenTail(session: CardSessionState): boolean {
  const item = tail(session);
  return Boolean(item && item.result == null);
}

function phaseForItem(session: CardSessionState, item: CardAttempt): CardSessionPhase {
  if (session.status === "completed") {
    return session.browsingHistory ? "review" : "completed";
  }
  if (item.result) {
    return "review";
  }
  if (item.revealedAt) {
    return "revealed";
  }
  return "hidden";
}

function finishSession(session: CardSessionState): CardSessionState {
  return {
    ...session,
    status: "completed",
    phase: "completed",
    pendingEvent: undefined,
    browsingHistory: false,
    revealHint: undefined,
  };
}

function blockedByContent(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  item: CardAttempt,
): CardSessionState | null {
  const meaning = findMeaningById(snapshot, item.meaningId);
  if (!meaning) {
    return {
      ...session,
      phase: "conflict",
      conflictMessage: "This meaning was deleted. End the session to continue.",
    };
  }
  if (meaning.contentRevision !== item.contentRevision) {
    return {
      ...session,
      phase: "conflict",
      conflictMessage: "This card changed and can no longer be graded. End the session and start again.",
    };
  }
  return null;
}

function answerVisible(item: CardAttempt): boolean {
  return item.revealedAt != null || item.result === "correct" || item.result === "incorrect";
}

function createPresentation(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  deps: PracticeEngineDeps,
): { item: CardAttempt; event: CardReviewEvent } | null {
  const now = deps.now();
  if (
    isCardClockBackward({
      knownObservedAt: snapshot.schedulerState.lastObservedAt,
      presentedAt: now,
      actionAt: now,
      previousActionAt: null,
    })
  ) {
    return null;
  }
  const excluded = new Set(session.items.map((item) => item.meaningId));
  const pool = eligibleCardMeanings(
    snapshot,
    session.mode,
    session.direction,
    session.selectedMeaningIds,
    now,
  ).filter((candidate) => !excluded.has(candidate.meaning.id));
  const chosen = selectCardMeaning(
    pool.map((candidate) => candidate.state),
    session.direction,
    snapshot.schedulerState.lastPresentedCardMeaningIds[session.direction],
  );
  if (!chosen) {
    return null;
  }
  const candidate = pool.find((item) => item.state.meaningId === chosen.meaningId);
  if (!candidate) {
    return null;
  }
  const classification = classifyCardPresentation(candidate.state, session.direction, now);
  const eventId = deps.createId();
  const attemptId = deps.createId();
  const captured = captureContent(snapshot, candidate.meaning, candidate.word);
  const item: CardAttempt = {
    itemId: deps.createId(),
    attemptId,
    meaningId: candidate.meaning.id,
    contentRevision: candidate.meaning.contentRevision,
    direction: session.direction,
    presentedAt: now,
    revealedAt: null,
    contextUsed: false,
    contextShownAt: null,
    wasDue: classification.wasDue,
    wasSpaced: classification.wasSpaced,
    isFirstAssessment: classification.isFirstAssessment,
    bucket: classification.wasDue ? "scheduled" : "extra",
    captured,
    result: null,
    heldReason: null,
    expectedStateRevision: candidate.state.stateRevision,
    presentationEventId: eventId,
    lastActionId: eventId,
    lastActionAt: now,
    gradeEventId: null,
    skipEventId: null,
  };
  return { item, event: buildEvent(snapshot, session, item, "presentation", now, eventId, null, true) };
}

function captureContent(snapshot: VocabularySnapshot, meaning: MeaningCard, word: WordEntry): CapturedCardContent {
  return {
    wordDisplay: word.displayText,
    normalizedText: word.normalizedText,
    type: meaning.type,
    shortLabel: meaning.shortLabel,
    definition: meaning.definition,
    example: meaning.example,
    russianHint: meaning.russianHint,
    memoryKey: meaning.memoryKey,
    collocations: [...meaning.collocations],
    ambiguous: meaningsForWord(snapshot, word.id).length > 1,
  };
}

function buildEvent(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  item: CardAttempt,
  actionKind: CardActionKind,
  actionAt: string,
  eventId: string,
  outcome: CardGradeOutcome | null,
  isPresentation: boolean,
): CardReviewEvent {
  const state = practiceStateFor(snapshot, item.meaningId);
  return {
    format: "cards",
    eventId,
    sessionId: session.sessionId,
    attemptId: item.attemptId,
    meaningId: item.meaningId,
    exerciseId: null,
    exerciseKind: null,
    direction: item.direction,
    actionKind,
    gradingMethod: actionKind === "grade" ? "self" : null,
    contextUsed: item.contextUsed,
    meaningContentRevision: item.contentRevision,
    expectedStateRevision: item.expectedStateRevision,
    presentedAt: item.presentedAt,
    actionAt,
    previousActionId: isPresentation ? null : item.lastActionId,
    previousActionAt: isPresentation ? null : item.lastActionAt,
    knownObservedAt: snapshot.schedulerState.lastObservedAt,
    outcome,
    wasDue: item.wasDue,
    wasSpaced: item.wasSpaced,
    isFirstAssessment: item.isFirstAssessment,
    scheduledOrExtra: item.bucket,
    cardPolicyVersion: CARD_POLICY_VERSION,
    previousTarget: cardTargetSnapshot(state, item.direction),
    resultingTarget: cardTargetSnapshot(state, item.direction),
  };
}

function queueAction(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  item: CardAttempt,
  actionKind: CardActionKind,
  outcome: CardGradeOutcome | null,
  deps: PracticeEngineDeps,
  patch: (item: CardAttempt, actionAt: string) => CardAttempt,
): { session: CardSessionState; event?: CardReviewEvent } {
  const actionAt = deps.now();
  const previousActionAt = item.lastActionAt;
  if (
    isCardClockBackward({
      knownObservedAt: snapshot.schedulerState.lastObservedAt,
      presentedAt: item.presentedAt,
      actionAt,
      previousActionAt,
    })
  ) {
    return { session: { ...session, phase: "clock_blocked", clockMessage: CLOCK_MESSAGE } };
  }
  const patched = patch(item, actionAt);
  const eventId = deps.createId();
  const event = buildEvent(snapshot, session, patched, actionKind, actionAt, eventId, outcome, false);
  const items = session.items.map((candidate) => (candidate.itemId === patched.itemId ? patched : candidate));
  return {
    event,
    session: {
      ...session,
      items,
      phase: "saving",
      pendingEvent: event,
      revealHint: undefined,
      persistError: undefined,
      clockMessage: undefined,
    },
  };
}

function landOn(
  snapshot: VocabularySnapshot,
  session: CardSessionState,
  cursor: number,
  deps: PracticeEngineDeps,
): { session: CardSessionState; event?: CardReviewEvent } {
  const item = session.items[cursor];
  if (!item) {
    return { session };
  }
  const moved: CardSessionState = {
    ...session,
    cursor,
    browsingHistory: session.status === "completed",
    revealHint: undefined,
    phase: "hidden",
  };
  const withPhase: CardSessionState = {
    ...moved,
    phase: phaseForItem(moved, item),
  };
  if (!answerVisible(item)) {
    return { session: withPhase };
  }
  const meaning = findMeaningById(snapshot, item.meaningId);
  if (!meaning || meaning.contentRevision !== item.contentRevision) {
    return { session: withPhase };
  }
  const actionAt = deps.now();
  if (
    isCardClockBackward({
      knownObservedAt: snapshot.schedulerState.lastObservedAt,
      presentedAt: item.presentedAt,
      actionAt,
      previousActionAt: item.lastActionAt,
    })
  ) {
    return { session: { ...withPhase, phase: "clock_blocked", clockMessage: CLOCK_MESSAGE } };
  }
  const eventId = deps.createId();
  const event = buildEvent(snapshot, withPhase, item, "revisit", actionAt, eventId, null, false);
  return {
    event,
    session: { ...withPhase, phase: "saving", pendingEvent: event },
  };
}
